import { doesUserHaveReleaseToggle } from '@skyspecs/release-utils-client';
import { getDefaultStore, useAtom } from 'jotai';
import { RESET, useAtomCallback } from 'jotai/utils';
import { useCallback, useEffect, useMemo } from 'react';

import { useFeatures } from 'utils/features';

import { releaseTogglesAtom, releaseTogglesDisabledAtom } from './account/atoms';

export const AERO_ENTERPRISE_UPLOAD = 'aero-enterprise-upload';
export const BLADE_INTERNAL_SCHEMATIC_VIEW = 'blade_internal_schematic_view';
export const BOP_INSPECTIONS = 'bop_inspections';
export const BULK_UPDATE_PICTURES = 'bulk_update_pictures';
export const COST_MANAGEMENT = 'cost_management';
export const COST_MANAGEMENT_CURRENCY = 'cost_management_currency';
export const CUSTOMIZABLE_DASHBOARD = 'customizable_dashboard';
export const DAMAGES_DB_MAINTENANCE_BANNER = 'damages_db_maintenance_banner';
export const DAMAGES_INBOX = 'damages_inbox';
export const ENHANCED_INSPECTION_STATUSES = 'enhanced_inspection_statuses';
export const EXECUTIVE_DASHBOARD = 'executive_dashboard';
export const TASK_LOGS_IN_WORK_ORDERS = 'task_logs_in_work_orders';
export const SKIP_INSPECTION_TASK_CREATION = 'skip_inspection_task_creation';
export const INSPECTION_MAP_VIEW = 'inspection_map_view';
export const INSPECTION_UPLOAD_SORT_FILTER_ADDITIONS = 'inspection_upload_sort_filter_additions';
export const IS_360_VIEWER = '360_viewer';
export const NEW_IMAGE_VIEWER = 'new_image_viewer';
export const RELEASE_NOTIFICATION = 'release_notification';
export const REMOVE_WORK_LOG_FIELD_LIMIT = 'remove-work-log-field-limit';
export const RVM_OPTIMIZATIONS = 'rvm_optimizations_2026';
export const SKYCRAWLER_UPLOAD = 'skycrawler-upload';
export const TASK_STEP_IMPROVEMENTS = 'task_step_improvements';
export const TEST_RT = 'test_rt';
export const USER_MANAGER = 'user_manager';
export const VENDOR_CREDITS = 'vendor_credits';

/**
 * When an internal user has the `INTERNAL_TESTER` feature flag, they are able
 * to enable and disable their use of release toggles. By default, release
 * toggles will be enabled. When disabled, the entry `user` will be added to
 * local storage and that value will be read when making GraphQL requests. When
 * set, the header `X-SkySpecs-IgnoreToggles` will be sent with `user`.
 */
export function useDisableReleaseToggles() {
  const { INTERNAL_TESTER } = useFeatures().features;
  const [disabledReleaseToggles, setDisabledReleaseToggles] = useAtom(releaseTogglesDisabledAtom);

  const releaseTogglesDisabled = useMemo(() => {
    return disabledReleaseToggles.includes('user');
  }, [disabledReleaseToggles]);

  const toggleDisabledReleaseToggles = useAtomCallback(
    useCallback((get, set) => {
      const existing = get(releaseTogglesDisabledAtom);
      const newValue = existing.includes('user')
        ? existing.filter(item => item !== 'user')
        : [...existing, 'user'];

      set(releaseTogglesDisabledAtom, newValue.length === 0 ? RESET : newValue);

      // add a slight delay so the action is not so jarring
      setTimeout(() => {
        // reload the window after making changes to make sure the app shows the
        // appropriate state
        window.location.reload();
      }, 500);
    }, [])
  );

  useEffect(() => {
    if (INTERNAL_TESTER) {
      if (disabledReleaseToggles.length === 0) {
        setDisabledReleaseToggles(RESET);
      } else {
        setDisabledReleaseToggles(disabledReleaseToggles);
      }
    } else {
      setDisabledReleaseToggles(RESET);
    }
  }, [disabledReleaseToggles, INTERNAL_TESTER, setDisabledReleaseToggles]);

  return {
    canManageReleaseToggles: INTERNAL_TESTER,
    releaseTogglesDisabled,
    toggleDisabledReleaseToggles,
  };
}

// Whenever possible get release toggles via hasReleaseToggle from useAccountContext().
// This function reads the jotai store directly and is not updated after the initial load.
const jotaiStore = getDefaultStore();
export function hasReleaseToggle(releaseToggleName: string) {
  const releaseToggles: string[] = jotaiStore.get(releaseTogglesAtom);
  return doesUserHaveReleaseToggle({ releaseToggles }, releaseToggleName);
}
